import { useEffect, useMemo, useState } from 'react';
import { useGetSettingsModuleConfigQuery } from '../../../state/domainRouterApi';
import { resolveRegistrationFee } from '../../../settings/registrationFee';
import { parseSettingsState } from '../../../settings/settingsState';
import { resolveAcademicYearLabel } from '../../../settings/academicYear';
import {
  getPublicApplySettings,
  type PublicApplySettings,
} from '../../../settings/publicSettingsClient';

const SETTINGS_MODULE_KEY = 'staff_settings';

const asRecord = (value: unknown): Record<string, unknown> => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
);

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const formatCurrency = (amount: number): string => currency.format(Number.isFinite(amount) ? amount : 0);

const isPresent = <T,>(value: T | null | undefined): value is T => value != null;

const formatDate = (value: string): string => {
  if (!value) return 'TBD';
  const isoDateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const parsed = isoDateOnly
    ? new Date(
      Number(isoDateOnly[1]),
      Number(isoDateOnly[2]) - 1,
      Number(isoDateOnly[3]),
    )
    : new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const displayGender = (gender: string): string => {
  if (gender === 'all') return '';
  return gender === 'boys' ? 'Boys' : 'Girls';
};

const toOfficeHours = (raw: unknown): string => {
  const text = String(raw ?? '').trim();
  return text || 'Contact office for hours';
};

const readText = (value: unknown): string => String(value ?? '').trim();

const formatAddress = (value: unknown): string => {
  if (typeof value === 'string') {
    return value.trim();
  }

  const record = asRecord(value);
  const parts = [
    readText(record.street),
    readText(record.city),
    readText(record.state),
    readText(record.zip ?? record.zipCode),
  ].filter(Boolean);
  return parts.join(', ');
};

const formatExtensionLabel = (value: unknown): string => {
  const record = asRecord(value);
  const prioritized = [
    readText(record.operationsManager),
    readText(record.executiveDirector),
  ].filter(Boolean);
  const fallback = Object.values(record)
    .map((entry) => readText(entry))
    .filter(Boolean);
  const extensions = Array.from(new Set((prioritized.length > 0 ? prioritized : fallback).slice(0, 2)));
  return extensions.join(' or ');
};

const formatPhoneDisplay = (phone: string, extension: string): string => {
  if (!phone) return extension;
  if (!extension) return phone;
  if (phone.toLowerCase().includes(extension.toLowerCase())) return phone;
  return `${phone} ${extension}`;
};

type SchoolPublicInfoOptions = {
  readMode?: 'authenticated' | 'public' | 'fallback';
  readFromBackend?: boolean;
};

export const useSchoolPublicInfo = (options: SchoolPublicInfoOptions = {}) => {
  const readMode = options.readMode
    ?? (options.readFromBackend === false ? 'fallback' : 'authenticated');
  const readFromBackend = readMode === 'authenticated';
  const readFromPublicBackend = readMode === 'public';
  const [publicSettings, setPublicSettings] = useState<{
    data?: PublicApplySettings;
    isLoading: boolean;
  }>({ isLoading: readFromPublicBackend });
  const settingsQuery = useGetSettingsModuleConfigQuery(
    { moduleKey: SETTINGS_MODULE_KEY },
    { skip: !readFromBackend },
  );

  useEffect(() => {
    if (!readFromPublicBackend) {
      return undefined;
    }

    let cancelled = false;
    setPublicSettings((current) => ({
      data: current.data,
      isLoading: true,
    }));
    void getPublicApplySettings()
      .then((data) => {
        if (!cancelled) {
          setPublicSettings({ data, isLoading: false });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPublicSettings({ isLoading: false });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [readFromPublicBackend]);

  const settingsState = useMemo(() => {
    const payload = readFromPublicBackend ? undefined : asRecord(settingsQuery.data).payload;
    return parseSettingsState(payload);
  }, [readFromPublicBackend, settingsQuery.data]);

  const publicData = readFromPublicBackend ? publicSettings.data : undefined;
  const publicOrganization = asRecord(publicData?.organization);
  const publicAcademicYear = asRecord(publicData?.academicYear);
  const publicAdmissionDates = asRecord(publicData?.admissionDates);
  const publicFinancialAid = asRecord(publicData?.financialAid);
  const publicFinancialAidCycle = asRecord(publicFinancialAid.cycle);
  const publicContactInfo = asRecord(publicData?.contactInfo);
  const organizationExt = asRecord(settingsState.organization.ext);
  const divisionContacts = asRecord(organizationExt.divisionContacts);
  const businessOffice = asRecord(divisionContacts.businessOffice);
  const publicContactPhone = readText(publicContactInfo.phone);
  const extPhone = readText(organizationExt.phone);
  const publicOrganizationPhone = readText(publicOrganization.phone);
  const settingsOrganizationPhone = readText(settingsState.organization.phone);
  const businessOfficePhone = readText(businessOffice.phone);
  const settingsPhone = extPhone || settingsOrganizationPhone || businessOfficePhone;
  const settingsPhoneExtension = extPhone
    ? readText(organizationExt.phoneExt)
    : settingsOrganizationPhone
      ? readText(settingsState.organization.phoneExt)
      : businessOfficePhone
        ? formatExtensionLabel(businessOffice.extensions)
        : '';
  const schoolEmail =
    readText(publicContactInfo.email)
    || readText(publicOrganization.emailFinancialAid)
    || readText(settingsState.organization.emailFinancialAid)
    || readText(businessOffice.email)
    || readText(publicOrganization.emailAdmissions)
    || readText(settingsState.organization.emailAdmissions)
    || readText(publicOrganization.emailSupport)
    || readText(settingsState.organization.emailSupport);
  const schoolPhone =
    publicContactPhone
    || publicOrganizationPhone
    || settingsPhone;
  const schoolPhoneDisplay = formatPhoneDisplay(
    schoolPhone,
    publicContactPhone
      ? ''
      : publicOrganizationPhone
        ? readText(publicOrganization.phoneExt)
        : settingsPhoneExtension,
  );
  const schoolAddress =
    formatAddress(publicContactInfo.address)
    || formatAddress(publicOrganization.address)
    || formatAddress(settingsState.organization.address)
    || formatAddress(businessOffice.address);
  const registrationFee = resolveRegistrationFee(settingsState);

  const feeSource = readFromPublicBackend && Array.isArray(publicData?.fees)
    ? publicData.fees
    : settingsState.fees;
  const fees = feeSource.filter(isPresent).map((fee) => ({
    id: fee.id,
    name: fee.name,
    amountLabel: formatCurrency(Number(fee.amount ?? 0)),
  }));

  const tuitionRateSource = readFromPublicBackend && Array.isArray(publicData?.tuitionRates)
    ? publicData.tuitionRates
    : settingsState.tuitionRates;
  const tuitionRates = tuitionRateSource.filter(isPresent).map((rate) => {
    const genderLabel = displayGender(readText(rate.gender));
    const rawGradeLabel = readText(rate.gradeLabel);
    const gradeLabel = genderLabel ? `${rawGradeLabel} ${genderLabel}` : rawGradeLabel;
    return {
      id: rate.id,
      gradeLabel,
      amountLabel: formatCurrency(Number(rate.amount ?? 0)),
    };
  });

  return {
    schoolName: readText(publicOrganization.name) || settingsState.organization.name,
    schoolLogo: readText(publicOrganization.logo) || settingsState.organization.logo,
    schoolMissionStatement:
      readText(publicOrganization.missionStatement) || settingsState.organization.missionStatement,
    schoolAddress,
    schoolPhone,
    schoolPhoneDisplay,
    schoolEmail,
    academicYearLabel: resolveAcademicYearLabel(
      readFromPublicBackend ? publicAcademicYear : settingsState.academicYear,
    ),
    applicationOpenDateLabel: formatDate(
      readText(publicAdmissionDates.openDate) || settingsState.admissionDates.openDate,
    ),
    onTimeDeadlineLabel: formatDate(
      readText(publicAdmissionDates.closeDate) || settingsState.admissionDates.closeDate,
    ),
    hardCutoffDeadlineLabel: formatDate(
      readText(publicAdmissionDates.decisionDate) || settingsState.admissionDates.decisionDate,
    ),
    financialAidOnTimeDeadlineLabel: formatDate(
      readText(publicFinancialAidCycle.onTimeDeadline) || settingsState.financialAid.cycle.onTimeDeadline,
    ),
    financialAidHardCutoffDeadlineLabel: formatDate(
      readText(publicFinancialAidCycle.hardCutoffDeadline) || settingsState.financialAid.cycle.hardCutoffDeadline,
    ),
    lateFeeLabel: formatCurrency(
      Number(publicFinancialAidCycle.lateFeeAmount ?? settingsState.financialAid.cycle.lateFeeAmount),
    ),
    fees,
    registrationFee,
    tuitionRates,
    contactInfo: {
      email: schoolEmail,
      phone: schoolPhone,
      hours: toOfficeHours(publicContactInfo.hours ?? businessOffice.hours),
    },
    isLoading: readFromPublicBackend ? publicSettings.isLoading : settingsQuery.isLoading,
  };
};
